import { createAsyncThunk } from "@reduxjs/toolkit";

import { getDeploymentSettingsRequest } from "Api/organizations/projects/environments/deployments/getDeploymentSetting.request";
import logger from "Libs/logger";
import { isJson } from "Libs/utils";

import {
  isDeploymentSettingsFetchedSelector,
  isLoadingDeploymentSettingsSelector
} from "../deployment.selectors";

import type { DeploymentSettings } from "../types";
import type { AsyncThunkOptionType } from "Reducers/types";

export const loadDeploymentSettings = createAsyncThunk<
  DeploymentSettings,
  {
    projectId: string;
    environmentId: string;
    organizationId: string;
    settings?: {
      /** See `LoadDeploymentCurrentProps["settings"]["reload"]`. */
      reload?: boolean;
    };
  },
  AsyncThunkOptionType
>(
  "app/environmentDeploymentSettings/load",
  async ({ projectId, environmentId }, { rejectWithValue }) => {
    try {
      const deploymentSettings = await getDeploymentSettingsRequest({
        projectId,
        environmentId
      });
      return deploymentSettings;
    } catch (error) {
      const errorMessage = isJson(error)
        ? error
        : "An error occurred while attempting to load deployment settings.";
      logger(errorMessage, {
        action: "load_deployment_settings"
      });

      return rejectWithValue(error as { code: number; message: string });
    }
  },
  {
    condition: (
      { organizationId, projectId, environmentId, settings = {} },
      { getState }
    ) => {
      const { reload } = settings;
      const state = getState();

      if (!organizationId || !projectId || !environmentId) {
        return false;
      }

      // Global flag, so `reload` overrides it; see loadDeploymentCurrent.
      if (!reload && isLoadingDeploymentSettingsSelector(state)) {
        return false;
      }

      /*
       * Ask once per environment. `useDeploymentType` re-mounts on every visit
       * to a deployment-aware page, and the endpoint answer only changes
       * through `updateDeploymentSettings`, which writes the new settings into
       * the store itself. Re-requesting on every render paired with the
       * deployment fetch to hammer the API (CON-1655).
       */
      if (
        !reload &&
        isDeploymentSettingsFetchedSelector(state, {
          organizationId,
          projectId,
          environmentId
        })
      ) {
        return false;
      }
    }
  }
);
